import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CalendarDays, RefreshCw } from "lucide-react"

import { MemberRsvpControl } from "@/components/guild-events/member-rsvp-control"
import { RosterColumns } from "@/components/guild-events/roster-columns"
import { EmptyState } from "@/components/shared/empty-state"
import { EventStatusBadge } from "@/components/shared/event-status-badge"
import { RouteErrorBoundary } from "@/components/shared/route-error-boundary"
import { StatusAlert } from "@/components/shared/status-alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DetailPageSkeleton } from "@/components/ui/page-skeletons"
import {
  deriveLifecycleStatus,
  getEventSignupPresentation,
  getMemberRsvpSummary,
  getRoleLabel,
} from "@/core/guild-events/labels"
import { formatGuildEventDateTimeWithTimezone } from "@/lib/guild-date-format"
import {
  type GuildEventRecord,
  type GuildEventSignupRecord,
  guildEventsApi,
} from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"

type MemberRsvpPresentation =
  | { canRespond: true }
  | { canRespond: false; message: string; rsvpSummary?: string }

export function getMemberRsvpPresentation(
  event: GuildEventRecord,
  mySignup: GuildEventSignupRecord | undefined,
  now: Date,
): MemberRsvpPresentation {
  if (!event.rsvpEnabled) {
    return {
      canRespond: false,
      message: "RSVPs are not enabled for this event.",
    }
  }

  const effectiveStatus =
    event.status === "scheduled" || event.status === "closed"
      ? deriveLifecycleStatus(
          {
            ...event,
            startAt: new Date(event.startAt),
            signupCloseAt:
              event.eventKind === "raid" ? new Date(event.signupCloseAt) : null,
          },
          now,
        )
      : event.status

  if (effectiveStatus === "archived" || effectiveStatus === "cancelled") {
    return {
      canRespond: false,
      message:
        effectiveStatus === "archived"
          ? "This event has been archived. RSVP changes are unavailable."
          : "This event has been cancelled.",
      rsvpSummary: mySignup
        ? getMemberRsvpSummary(mySignup.state, mySignup.roleKey)
        : undefined,
    }
  }

  if (
    getEventSignupPresentation(
      {
        ...event,
        startAt: new Date(event.startAt),
        signupCloseAt:
          event.eventKind === "raid" ? new Date(event.signupCloseAt) : null,
      },
      now,
    ).isOpen
  ) {
    return { canRespond: true }
  }

  const message =
    effectiveStatus === "started"
      ? "This event has started."
      : effectiveStatus === "announcement_missing"
        ? "This event’s Discord announcement is unavailable. RSVP changes are unavailable."
        : "Signups are closed for this event."

  return {
    canRespond: false,
    message,
    rsvpSummary: mySignup
      ? getMemberRsvpSummary(mySignup.state, mySignup.roleKey)
      : undefined,
  }
}

export const Route = createFileRoute("/_authed/events/$eventId")({
  component: MemberEventDetailRoute,
  errorComponent: RouteErrorBoundary,
})

function MemberEventDetailRoute() {
  const { eventId } = Route.useParams()
  const navigate = useNavigate()
  const { auth } = Route.useRouteContext()

  const detailQuery = useQuery({
    queryKey: guildEventsKeys.memberDetail(eventId),
    queryFn: () => guildEventsApi.getMemberEventDetail(eventId),
  })

  const event = detailQuery.data?.event
  const signups = detailQuery.data?.signups ?? []

  if (detailQuery.isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading event details">
        <DetailPageSkeleton sections={2} />
      </div>
    )
  }

  if (detailQuery.isError) {
    return (
      <div className="grid gap-4 md:gap-5">
        <StatusAlert tone="danger">
          <div className="flex items-center justify-between gap-3">
            <span>Failed to load event details. Please try again.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void detailQuery.refetch()}
              disabled={detailQuery.isFetching}
            >
              <RefreshCw
                className={cn(
                  "size-3.5",
                  detailQuery.isFetching && "animate-spin",
                )}
              />
              Retry
            </Button>
          </div>
        </StatusAlert>
      </div>
    )
  }

  if (!event) {
    return (
      <EmptyState
        icon={<CalendarDays className="size-5" />}
        title="Event not found"
        description="This event may have been removed or the link is incorrect."
        tone="muted"
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: "/events" })}
          >
            Back to events
          </Button>
        }
      />
    )
  }

  const eventPresentation = getMemberEventDetailPresentation(event)

  const mySignup = event.rsvpEnabled
    ? signups.find((signup) => signup.memberId === auth?.user?.userId)
    : undefined
  const rsvpPresentation = getMemberRsvpPresentation(
    event,
    mySignup,
    new Date(),
  )
  const attending = signups.filter((signup) => signup.state === "attending")
  const tentative = signups.filter((signup) => signup.state === "tentative")
  const notAttending = signups.filter(
    (signup) => signup.state === "not_attending",
  )

  return (
    <div
      className="grid gap-4 md:gap-5"
      aria-busy={detailQuery.isFetching ? "true" : undefined}
    >
      <Card>
        <CardContent className="grid gap-4 pt-5">
          <div className="flex items-start gap-2">
            <h1 className="min-w-0 text-xl font-semibold break-words">
              {event.title}
            </h1>
            <EventStatusBadge status={event.status} />
          </div>
          {event.description ? (
            <div className="text-sm text-muted-foreground">
              {event.description}
            </div>
          ) : null}
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="grid gap-1">
              <dt className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                When
              </dt>
              <dd>{eventPresentation.when}</dd>
              {rsvpPresentation.canRespond ? (
                eventPresentation.closesAt ? (
                  <dd className="text-xs text-muted-foreground">
                    Signups close {eventPresentation.closesAt}
                  </dd>
                ) : (
                  <dd className="text-xs text-muted-foreground">
                    RSVPs stay open until start.
                  </dd>
                )
              ) : null}
            </div>
            <div className="grid gap-1">
              <dt className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                What
              </dt>
              <dd>{eventPresentation.kind}</dd>
              <dd className="text-xs text-muted-foreground">
                {eventPresentation.scope}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {event.rsvpEnabled ? (
        rsvpPresentation.canRespond ? (
          <MemberRsvpControl event={event} mySignup={mySignup} />
        ) : (
          <StatusAlert tone="info">
            <div className="grid gap-1">
              <div>{rsvpPresentation.message}</div>
              {rsvpPresentation.rsvpSummary ? (
                <div>{rsvpPresentation.rsvpSummary}</div>
              ) : null}
            </div>
          </StatusAlert>
        )
      ) : null}
      {event.rsvpEnabled ? (
        <Card className="bg-[#c58e9a]/10! bg-none!">
          <CardContent className="grid gap-4 pt-5 md:grid-cols-3">
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Attending</div>
                <Badge variant="outline">{attending.length}</Badge>
              </div>
              <RosterColumns
                signups={attending}
                variant="member"
                viewerMemberId={auth?.user?.userId}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Tentative</div>
                <Badge variant="outline">{tentative.length}</Badge>
              </div>
              <RosterColumns
                signups={tentative}
                variant="member"
                viewerMemberId={auth?.user?.userId}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Not Attending</div>
                <Badge variant="outline">{notAttending.length}</Badge>
              </div>
              <RosterColumns
                signups={notAttending}
                variant="member"
                viewerMemberId={auth?.user?.userId}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
export function getMemberEventDetailPresentation(event: GuildEventRecord) {
  return {
    when: formatGuildEventDateTimeWithTimezone(event.startAt),
    closesAt:
      event.eventKind === "raid"
        ? formatGuildEventDateTimeWithTimezone(event.signupCloseAt)
        : null,
    kind: event.eventKind === "raid" ? "Raid" : "Attendance",
    scope:
      event.eventKind === "raid"
        ? `Roles: ${event.eventRoles.map(getRoleLabel).join(", ")}`
        : "Guild-wide RSVP",
  }
}
